import { z } from "zod";

export const EGlobalSearchCategory = {
  Transactions: "transactions",
  People: "people",
  EmployerChecks: "employer_checks",
  EmployeeChecks: "employee_checks",
  ContractorPayments: "contractor_payments",
  ContractorPaymentGroups: "contractor_payment_groups",
} as const;

export type TGlobalSearchCategory =
  (typeof EGlobalSearchCategory)[keyof typeof EGlobalSearchCategory];

export const globalSearchRequestSchema = z.object({
  companyID: z.string().min(1),
  q: z.string().min(1),
  category: z
    .enum([
      "transactions",
      "people",
      "employer_checks",
      "employee_checks",
      "contractor_payments",
      "contractor_payment_groups",
    ])
    .optional(),
  limit: z.coerce.number().int().min(1).max(20).optional().default(5),
});

export type TGlobalSearchRequest = z.infer<typeof globalSearchRequestSchema>;

export const globalSearchTransactionResultSchema = z.object({
  transactionID: z.string(),
  counterpartyName: z.string(),
  transactionType: z.string(),
  method: z.string().optional(),
  amount: z.number(),
  date: z.string().optional(),
  status: z.string().optional(),
});

export type TGlobalSearchTransactionResult = z.infer<
  typeof globalSearchTransactionResultSchema
>;

export const globalSearchPersonResultSchema = z.object({
  workerID: z.string(),
  fullName: z.string(),
  workerType: z.enum(["employee", "contractor"]),
  email: z.string().optional(),
});

export type TGlobalSearchPersonResult = z.infer<
  typeof globalSearchPersonResultSchema
>;

export const globalSearchEmployerCheckResultSchema = z.object({
  employerCheckID: z.string(),
  checkDate: z.string().optional(),
  reportingPeriodStart: z.string().optional(),
  reportingPeriodEnd: z.string().optional(),
  status: z.string().optional(),
  netPay: z.number().optional(),
  grossPay: z.number().optional(),
});

export type TGlobalSearchEmployerCheckResult = z.infer<
  typeof globalSearchEmployerCheckResultSchema
>;

export const globalSearchEmployeeCheckResultSchema = z.object({
  employeeCheckID: z.string(),
  employerCheckID: z.string().optional(),
  employeeID: z.string(),
  employeeName: z.string(),
  checkDate: z.string().optional(),
  netPay: z.number().optional(),
  status: z.string().optional(),
});

export type TGlobalSearchEmployeeCheckResult = z.infer<
  typeof globalSearchEmployeeCheckResultSchema
>;

export const globalSearchContractorPaymentResultSchema = z.object({
  contractorPaymentID: z.string(),
  contractorID: z.string(),
  contractorName: z.string(),
  payDate: z.string().optional(),
  amount: z.number().optional(),
  status: z.string().optional(),
});

export type TGlobalSearchContractorPaymentResult = z.infer<
  typeof globalSearchContractorPaymentResultSchema
>;

export const globalSearchContractorPaymentGroupResultSchema = z.object({
  contractorPaymentGroupID: z.string(),
  payDate: z.string().optional(),
  speed: z.string().optional(),
  status: z.string().optional(),
  companyCashRequirement: z.number().optional(),
  companyDebit: z.number().optional(),
});

export type TGlobalSearchContractorPaymentGroupResult = z.infer<
  typeof globalSearchContractorPaymentGroupResultSchema
>;

export const globalSearchResponseSchema = z.object({
  transactions: z.array(globalSearchTransactionResultSchema).optional(),
  people: z.array(globalSearchPersonResultSchema).optional(),
  employerChecks: z.array(globalSearchEmployerCheckResultSchema).optional(),
  employeeChecks: z.array(globalSearchEmployeeCheckResultSchema).optional(),
  contractorPayments: z
    .array(globalSearchContractorPaymentResultSchema)
    .optional(),
  contractorPaymentGroups: z
    .array(globalSearchContractorPaymentGroupResultSchema)
    .optional(),
});

export type TGlobalSearchResponse = z.infer<typeof globalSearchResponseSchema>;
